import React, { Component } from 'react';
import BlockUi from 'react-block-ui';
import { Button } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { postAPI } from '../../Base/API';
import { handleNotification } from '../../Base/Common/Notification';
import { blankTemplateHtml, blankTemplateCss } from './StripoBlankTemplate';

const STRIPO_SCRIPT_ID = 'UiEditorScript';
const STRIPO_SCRIPT_SRC = 'https://plugins.stripo.email/resources/uieditor/latest/UIEditor.js';

//Stripo plugin v2 embedded editor. Loads the UIEditor script, gets the auth token from the backend
//(the SecretKey never reaches the browser) and exposes saveDesign/compileEmail through a ref.
class StripoEditor extends Component {
    constructor(props) {
        super(props);

        this.editorContainerRef = React.createRef();

        this.state = {
            scriptLoaded: !!window.UIEditor,
            scriptError: false,
            block: true
        };
    }

    componentDidMount() {
        this.loadScript();
    }

    componentDidUpdate(prevProps) {
        //Language tab switch or version restore: reload the editor with the new design
        if ((prevProps.templateId !== this.props.templateId || prevProps.designKey !== this.props.designKey) && this.state.scriptLoaded) {
            this.setState({ block: true }, this.initEditor);
        }
    }

    componentWillUnmount() {
        this._unmounted = true;

        if (this._initEditorTimeout) {
            clearTimeout(this._initEditorTimeout);
            this._initEditorTimeout = null;
        }

        if (window.UIEditor && typeof window.UIEditor.removeEditor === 'function') {
            window.UIEditor.removeEditor();
        }

        const script = document.getElementById(STRIPO_SCRIPT_ID);
        if (script) {
            document.body.removeChild(script);
        }
    }

    loadScript = () => {
        //The plugin survives the removal of the script tag, so skip the injection when it is already available
        if (window.UIEditor) {
            this.setState({ scriptLoaded: true, scriptError: false }, this.initEditor);
            return;
        }

        //Drop any leftover tag from a previous failed attempt before retrying
        const previous = document.getElementById(STRIPO_SCRIPT_ID);
        if (previous) {
            document.body.removeChild(previous);
        }

        const script = document.createElement('script');
        script.src = STRIPO_SCRIPT_SRC;
        script.id = STRIPO_SCRIPT_ID;
        script.type = 'module';
        script.onload = () => {
            if (!this._unmounted) {
                this.setState({ scriptLoaded: true }, this.initEditor);
            }
        };
        script.onerror = () => {
            if (!this._unmounted) {
                this.setState({ scriptError: true, block: false });
            }
        };
        document.body.appendChild(script);
    }

    retry = () => {
        this.setState({ scriptError: false, scriptLoaded: false, block: true }, this.loadScript);
    }

    getAuth = () => {
        return new Promise((resolve, reject) => postAPI(result => {
            const { data, error } = result;

            if (error) {
                const errorMessage = [{ message: error.message, stack: error.stack, messageType: 'danger' }];
                this.setState({ error: errorMessage, block: false });
                reject(error);
                return;
            }
            if (data) {
                if (data.errors && data.errors.length > 0) {
                    handleNotification(data);
                    this.setState({ block: false });
                    reject(new Error('StripoToken request returned errors'));
                    return;
                }
                if (data.response && data.response.length > 0) {
                    resolve(data.response[0]);
                    return;
                }
            }
            reject(new Error('StripoToken request returned no auth data'));
        }, `/api/Voucher/v1/Notification/StripoToken`, {}));
    }

    initEditor = () => {
        const container = this.editorContainerRef.current;

        if (!container || !window.UIEditor) {
            this.setState({ block: false });
            return;
        }

        if (typeof window.UIEditor.removeEditor === 'function') {
            window.UIEditor.removeEditor();
        }

        //Hard fallback: if the editor never fires onTemplateLoaded, clear the spinner so the UI doesn't hang
        if (this._initEditorTimeout) {
            clearTimeout(this._initEditorTimeout);
        }
        this._initEditorTimeout = setTimeout(() => {
            this._initEditorTimeout = null;
            if (this.state.block && !this._unmounted) {
                this.setState({ block: false });
            }
        }, 20000);

        this.getAuth().then(auth => {
            if (this._unmounted) {
                return;
            }

            try {
                window.UIEditor.initEditor(container, {
                    locale: 'en',
                    forceRecreate: true,
                    disableRemoteCursors: true,
                    //Designs without esd- markup (empty, compiled or hand written html) load fully locked,
                    //so fall back to the blank Stripo skeleton in that case
                    html: this.props.html && this.props.html.includes('esd-') ? this.props.html : blankTemplateHtml,
                    css: this.props.css || blankTemplateCss,
                    metadata: {
                        //always use strings on emailId
                        emailId: String(this.props.templateId),
                        userId: auth.userId
                    },
                    mobileViewEnabled: true,
                    enableNativeSpellChecker: true,
                    supportOutlookButtonsByDefault: true,
                    onTemplateLoaded: () => {
                        if (this._initEditorTimeout) {
                            clearTimeout(this._initEditorTimeout);
                            this._initEditorTimeout = null;
                        }
                        if (!this._unmounted) {
                            this.setState({ block: false });
                        }
                    },
                    onTokenRefreshRequest: (callback) => {
                        this.getAuth()
                            .then(val => callback(val.token))
                            .catch(() => { /* token refresh failed; the editor session will expire */ });
                    }
                });
            }
            catch (error) {
                console.error('Error initializing Stripo editor:', error);
                if (this._initEditorTimeout) {
                    clearTimeout(this._initEditorTimeout);
                    this._initEditorTimeout = null;
                }
                this.setState({ block: false });
            }
        }).catch(() => {
            //getAuth already surfaced the error to the user; just stop the spinner
            if (this._initEditorTimeout) {
                clearTimeout(this._initEditorTimeout);
                this._initEditorTimeout = null;
            }
            if (!this._unmounted) {
                this.setState({ block: false });
            }
        });
    }

    //Returns the editable design source (html + css) without compiling. Used by Save Design.
    saveDesign = (callback) => {
        if (window.StripoEditorApi && window.StripoEditorApi.actionsApi) {
            window.StripoEditorApi.actionsApi.getTemplateData((templateData) => {
                callback(templateData.html, templateData.css || '');
            });
        }
        else {
            //Editor not initialized (still loading or failed); let the caller unblock and react
            callback(null, null);
        }
    }

    //Returns the final send-ready compiled html. Used by Compile and Publish.
    compileEmail = (callback) => {
        if (window.StripoEditorApi && window.StripoEditorApi.actionsApi) {
            window.StripoEditorApi.actionsApi.compileEmail({
                callback: (error, html) => callback(error, html)
            });
        }
        else {
            callback(new Error('Stripo editor is not initialized'), null);
        }
    }

    render() {
        if (this.state.scriptError) {
            return (
                <div className="text-center py-5">
                    <h6><FormattedMessage id="StripoEditor.ScriptLoadError" /></h6>
                    <Button className="btn-host btn-sm mt-3" onClick={this.retry}>
                        <FormattedMessage id="StripoEditor.Retry" />
                    </Button>
                </div>
            );
        }

        return (
            <BlockUi tag="div" blocking={this.state.block}>
                <div ref={this.editorContainerRef} style={{ height: '70vh' }} />
            </BlockUi>
        );
    }
}

export default StripoEditor;
